import { useEffect, useState } from "react";
import useSWR from "swr";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg, fetcher, formatDate } from "../../lib/api";
import { PageHeader, GoldButton, GhostButton } from "../../components/admin/AdminUI";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import ConfirmDelete from "../../components/admin/ConfirmDelete";

function CustomerSheet({ id, onClose, onChanged }) {
  const { data, mutate } = useSWR(id ? `/admin/customers/${id}` : null, fetcher);
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    setNotes(data?.notes || "");
  }, [data]);

  const save = async () => {
    try {
      await api.put(`/admin/customers/${id}/notes`, { notes });
      toast.success("Observações salvas");
      mutate();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success("Dados do cliente excluídos");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setConfirm(false);
  };

  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-l border-[#D4AF37]/20 bg-[#0B0B0C] sm:max-w-lg" data-testid="customer-sheet">
        <SheetTitle className="font-serif text-3xl font-medium" data-testid="customer-sheet-phone">{data?.phone_formatted || "…"}</SheetTitle>
        <SheetDescription>Cadastrado em {formatDate(data?.created_at, true)}</SheetDescription>
        {data && (
          <div className="mt-6 space-y-6 text-sm">
            <div className="grid grid-cols-2 gap-3">
              {[["Último acesso", formatDate(data.last_seen_at, true)], ["Visitas", data.visits], ["Solicitações", data.request_count || 0], ["Ofertas", data.promo_consent ? `Aceita (${formatDate(data.consent_at)})` : "Não aceita"]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-white/5 bg-[#141417] p-3"><p className="text-[11px] uppercase tracking-wider text-[#A1A1AA]">{k}</p><p className="mt-1">{v}</p></div>
              ))}
            </div>
            <div>
              <p className="mb-2 text-xs uppercase tracking-[0.15em] text-[#A1A1AA]">Observações administrativas</p>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="customer-notes-input" />
              <GoldButton onClick={save} className="mt-3" data-testid="customer-notes-save">Salvar observações</GoldButton>
            </div>
            <div>
              <p className="mb-2 text-xs uppercase tracking-[0.15em] text-[#A1A1AA]">Histórico de solicitações</p>
              {data.requests.length === 0 && <p className="text-[#A1A1AA]">Nenhuma solicitação registrada.</p>}
              <div className="space-y-2">
                {data.requests.map((r) => (
                  <div key={r.id} className="rounded-xl border border-white/5 bg-[#141417] p-3">
                    <div className="flex justify-between text-xs text-[#A1A1AA]"><span>{formatDate(r.created_at, true)}</span><span className="text-[#F3E5AB]">{brl(r.total)}</span></div>
                    <p className="mt-1">{r.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p>
                  </div>
                ))}
              </div>
            </div>
            <GhostButton onClick={() => setConfirm(true)} className="text-red-400" data-testid="customer-delete-button"><Trash2 className="h-4 w-4" />Excluir dados do cliente (LGPD)</GhostButton>
          </div>
        )}
        <ConfirmDelete open={confirm} onCancel={() => setConfirm(false)} onConfirm={remove} title="Excluir cliente?" description="O telefone e os dados pessoais serão apagados; as solicitações ficam anônimas." />
      </SheetContent>
    </Sheet>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const { data, mutate } = useSWR(`/admin/customers?q=${encodeURIComponent(query)}&page=${page}`, fetcher);

  const exportCsv = async (consentOnly) => {
    try {
      const res = await api.get(`/admin/customers/export?consent_only=${consentOnly}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = consentOnly ? "clientes-consentimento.csv" : "clientes.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao exportar.");
    }
  };

  return (
    <div data-testid="admin-customers">
      <PageHeader title="Clientes" subtitle={`${data?.total ?? 0} clientes cadastrados`}>
        <GhostButton onClick={() => exportCsv(true)} data-testid="customers-export-consent"><Download className="h-4 w-4" />Exportar com consentimento</GhostButton>
        <GhostButton onClick={() => exportCsv(false)} data-testid="customers-export-all"><Download className="h-4 w-4" />Exportar todos</GhostButton>
      </PageHeader>
      <p className="-mt-4 mb-6 text-xs text-[#A1A1AA]">Para campanhas promocionais, use somente a lista “com consentimento” (LGPD).</p>
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(q); }} className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A1A1AA]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar pelo telefone" className="pl-9" data-testid="customers-search-input" />
      </form>
      <div className="overflow-x-auto rounded-2xl border border-white/5">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-[#141417] text-left text-[11px] uppercase tracking-wider text-[#A1A1AA]">
            <tr><th className="p-4">Telefone</th><th className="p-4">Cadastro</th><th className="p-4">Último acesso</th><th className="p-4">Solicitações</th><th className="p-4">Ofertas</th></tr>
          </thead>
          <tbody>
            {(data?.items || []).map((c) => (
              <tr key={c.id} onClick={() => setSelected(c.id)} className="cursor-pointer border-t border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`customer-row-${c.id}`}>
                <td className="p-4 font-medium">{c.phone_formatted}{c.notes && <span className="ml-2 text-[10px] text-[#D4AF37]">• obs.</span>}</td>
                <td className="p-4 text-[#A1A1AA]">{formatDate(c.created_at)}</td>
                <td className="p-4 text-[#A1A1AA]">{formatDate(c.last_seen_at, true)}</td>
                <td className="p-4">{c.request_count || 0}</td>
                <td className="p-4">{c.promo_consent ? <span className="text-emerald-400">Aceita</span> : <span className="text-[#A1A1AA]">Não</span>}</td>
              </tr>
            ))}
            {data?.items?.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#A1A1AA]">Nenhum cliente encontrado.</td></tr>}
          </tbody>
        </table>
      </div>
      {data?.pages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm">
          <GhostButton disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="customers-prev">Anterior</GhostButton>
          <span className="text-[#A1A1AA]">{page} / {data.pages}</span>
          <GhostButton disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="customers-next">Próxima</GhostButton>
        </div>
      )}
      <CustomerSheet id={selected} onClose={() => setSelected(null)} onChanged={mutate} />
    </div>
  );
}
